import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

const TodoFloatingButton = ({ 
  todos = [], 
  onToggleTodo, 
  onAddTodo, 
  onRemoveTodo,
  getPriorityColor, 
  getCategoryLabel 
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [quickTaskText, setQuickTaskText] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  const pendingTodos = todos.filter(todo => !todo.completed);
  const completedTodos = todos.filter(todo => todo.completed);

  // Log updates for debugging
  useEffect(() => {
    console.log('📋 Todo list updated:', { total: todos.length, pending: pendingTodos.length, completed: completedTodos.length });
  }, [todos.length, pendingTodos.length, completedTodos.length]);

  const handleQuickAdd = async () => {
    if (quickTaskText.trim() && !isAdding) {
      setIsAdding(true);
      try {
        await onAddTodo(quickTaskText, 'medium', 'personal');
        setQuickTaskText('');
      } catch (error) {
        console.error('Error adding todo:', error);
      } finally {
        setIsAdding(false);
      }
    }
  };

  const handleToggle = async (id) => {
    try {
      await onToggleTodo(id);
    } catch (error) {
      console.error('Error toggling todo:', error);
    }
  };

  const handleRemove = async (id) => {
    try {
      await onRemoveTodo(id);
    } catch (error) {
      console.error('Error removing todo:', error);
    }
  };

  return (
    <>
      {/* Hidden Trigger Button for Modal */}
      <button
        id="todo-fab-button"
        onClick={() => setIsModalOpen(true)}
        className="hidden"
      ></button>

      {/* Side Drawer Modal - Portal to Body */}
      {isModalOpen && createPortal(
        <div 
          key={`todo-modal-${todos.length}`}
          className="fixed inset-0 bg-black bg-opacity-30 transition-opacity"
          style={{ zIndex: 9999 }}
          onClick={() => setIsModalOpen(false)}
        >
          {/* Side Drawer - Slides from Right */}
          <div 
            className="fixed top-0 right-0 bottom-0 w-full max-w-md bg-white shadow-2xl flex flex-col overflow-hidden animate-slide-right"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header - Full Width */}
            <div className="flex items-center justify-between p-4 md:p-6 border-b border-gray-200 bg-gradient-to-r from-green-50 to-emerald-50">
              <div className="flex items-center gap-3">
                <div className="p-2 md:p-3 bg-green-100 rounded-xl">
                  <svg className="w-6 h-6 md:w-7 md:h-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-xl md:text-2xl font-bold text-gray-800">My Tasks</h2>
                  <p className="text-sm text-gray-600">
                    {pendingTodos.length} pending · {completedTodos.length} completed
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsModalOpen(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <svg className="w-6 h-6 md:w-7 md:h-7 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Quick Add Section */}
            <div className="p-4 md:p-6 border-b border-gray-100 bg-white">
              <div className="flex gap-2 md:gap-3">
                <input
                  type="text"
                  value={quickTaskText}
                  onChange={(e) => setQuickTaskText(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleQuickAdd()}
                  placeholder="Add a new task..."
                  className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm md:text-base"
                  autoFocus
                />
                <button
                  onClick={handleQuickAdd}
                  disabled={isAdding}
                  className="px-6 py-3 bg-green-500 text-white rounded-xl hover:bg-green-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed text-sm md:text-base"
                >
                  {isAdding ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Adding
                    </div>
                  ) : (
                    'Add'
                  )}
                </button>
              </div>
            </div>

            {/* Task List - Scrollable */}
            <div className="flex-1 overflow-y-auto p-4 md:p-6">
              {/* Pending Tasks */}
              {pendingTodos.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                    Pending Tasks ({pendingTodos.length})
                  </h3>
                  <div className="space-y-2">
                    {pendingTodos.map((todo) => (
                      <div
                        key={todo.id}
                        className="p-3 bg-white border border-gray-200 rounded-xl hover:shadow-md transition-all duration-200 flex items-start gap-3"
                      >
                        <button
                          onClick={() => handleToggle(todo.id)}
                          className="flex-shrink-0 w-6 h-6 border-2 border-green-500 rounded-md flex items-center justify-center hover:bg-green-50 transition-colors mt-0.5"
                        >
                          {todo.completed && (
                            <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </button>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-800">
                            {todo.text}
                          </p>
                          <div className="flex items-center gap-2 mt-2">
                            {getCategoryLabel && (
                              <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full font-medium">
                                {getCategoryLabel(todo.category)}
                              </span>
                            )}
                            {getPriorityColor && (
                              <span className={`text-xs px-2 py-1 rounded-full font-medium ${getPriorityColor(todo.priority)}`}>
                                {todo.priority}
                              </span>
                            )}
                          </div>
                        </div>
                        {onRemoveTodo && (
                          <button
                            onClick={() => handleRemove(todo.id)}
                            className="flex-shrink-0 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Completed Tasks */}
              {completedTodos.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                    Completed ({completedTodos.length})
                  </h3>
                  <div className="space-y-2">
                    {completedTodos.map((todo) => (
                      <div
                        key={todo.id}
                        className="p-3 bg-gray-50 border border-gray-200 rounded-xl flex items-start gap-3 opacity-60"
                      >
                        <button
                          onClick={() => onToggleTodo(todo.id)}
                          className="flex-shrink-0 w-6 h-6 bg-green-500 rounded-md flex items-center justify-center hover:bg-green-600 transition-colors mt-0.5"
                        >
                          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </button>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-500 line-through">
                            {todo.text}
                          </p>
                        </div>
                        {onRemoveTodo && (
                          <button
                            onClick={() => handleRemove(todo.id)}
                            className="flex-shrink-0 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Empty State */}
              {todos.length === 0 && (
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-gray-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                    <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">No Tasks Yet!</h3>
                  <p className="text-sm text-gray-500 mb-4">Start adding tasks to stay organized</p>
                </div>
              )}
            </div>

            {/* Footer - Quick Stats */}
            {todos.length > 0 && (
              <div className="p-4 md:p-6 border-t border-gray-200 bg-gray-50">
                <div className="flex items-center justify-around text-center">
                  <div>
                    <div className="text-lg font-bold text-gray-800">{todos.length}</div>
                    <div className="text-xs text-gray-600">Total</div>
                  </div>
                  <div className="w-px h-8 bg-gray-300"></div>
                  <div>
                    <div className="text-lg font-bold text-orange-600">{pendingTodos.length}</div>
                    <div className="text-xs text-gray-600">Pending</div>
                  </div>
                  <div className="w-px h-8 bg-gray-300"></div>
                  <div>
                    <div className="text-lg font-bold text-green-600">{completedTodos.length}</div>
                    <div className="text-xs text-gray-600">Done</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}

      {/* Animation Styles */}
      <style>{`
        @keyframes slide-right {
          from {
            transform: translateX(100%);
          }
          to {
            transform: translateX(0);
          }
        }
        
        .animate-slide-right {
          animation: slide-right 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
      `}</style>
    </>
  );
};

export default TodoFloatingButton;

